import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import API from '../api';
import {
  addIntegrationConnection,
  createIntegrationConnection,
  deleteIntegrationConnection,
  getExternalBooking,
  getExternalBookings,
  getIntegrationConnections,
  getIntegrationOverview,
  getIntegrationConflicts,
  getIntegrationRuns,
  rotateCalendarFeed,
  revokeCalendarFeed,
  setCalendarFeed,
  clearCalendarFeed,
  markExternalBookingViewed,
  removeIntegrationConnection,
  replaceExternalBooking,
  replaceExternalBookingViewed,
  replaceIntegrationConnection,
  setExternalBookings,
  setExternalBookingsError,
  setIntegrationConnections,
  setIntegrationOverview,
  setIntegrationConflicts,
  setIntegrationError,
  setIntegrationRuns,
  setSelectedExternalBooking,
  updateExternalBooking,
  updateIntegrationConnection,
  copyCalendarFeedUrl,
  finishCopyCalendarFeedUrl,
} from '../reducers/integrationSlice';
import callCheckingAuth from './callCheckingAuth';
import {
  ExternalBooking,
  IntegrationConflict,
  IntegrationConnection,
  IntegrationConnectionResponse,
  IntegrationSyncRun,
  IntegrationSyncRunResponse,
  ExternalBookingResponse,
  IntegrationConflictResponse,
  IntegrationOverviewItem,
  CalendarFeedUrlResponse,
  CalendarFeed,
  CalendarFeedRevokeResponse,
} from '../types/integrationTypes';
import { MessageTypes } from '../../utils/@globalTypes';
import { setMessage } from '../reducers/pageSlice';

const getResults = <T>(data: T[] | { results: T[] } | undefined): T[] =>
  Array.isArray(data) ? data : data?.results || [];

const errorText = (status?: number, fallback = 'Ошибка интеграции'): string => {
  if (status === 400) return 'Проверьте введённые данные';
  if (status === 403) return 'Недостаточно прав для выполнения операции';
  if (status === 404) return 'Интеграция или событие не найдено';
  if (status === 409) return 'Операция недоступна: данные используются другой записью';
  return fallback;
};

function* handleError(status?: number, fallback?: string) {
  const message = errorText(status, fallback);
  yield put(setIntegrationError(message));
  yield put(
    setMessage({ status: MessageTypes.ERROR, message, code: status || 500 }),
  );
}

export function* getIntegrationConnectionsWorker() {
  const response: ApiResponse<IntegrationConnectionResponse> = yield callCheckingAuth(
    API.getIntegrationConnectionsRequest,
  );
  if (response.ok && response.data) {
    yield put(setIntegrationConnections(getResults(response.data)));
  } else {
    yield* handleError(response.status, 'Не удалось загрузить подключения');
  }
}

export function* getIntegrationOverviewWorker() {
  const response: ApiResponse<IntegrationOverviewItem[]> =
    yield callCheckingAuth(API.getIntegrationOverviewRequest);
  if (response.ok && response.data) {
    yield put(setIntegrationOverview(response.data));
  } else {
    yield* handleError(response.status, 'Не удалось загрузить интеграции');
  }
}

export function* createIntegrationConnectionWorker(
  action: PayloadAction<any>,
) {
  const response: ApiResponse<IntegrationConnection> = yield callCheckingAuth(
    API.createIntegrationConnectionRequest,
    action.payload,
  );
  if (response.ok && response.data) {
    yield put(addIntegrationConnection(response.data));
    yield put(getIntegrationOverview());
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Подключение сохранено',
        code: response.status || 201,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось создать подключение');
  }
}

export function* updateIntegrationConnectionWorker(
  action: PayloadAction<{ id: number; data: any }>,
) {
  const response: ApiResponse<IntegrationConnection> = yield callCheckingAuth(
    API.updateIntegrationConnectionRequest,
    action.payload.id,
    action.payload.data,
  );
  if (response.ok && response.data) {
    yield put(replaceIntegrationConnection(response.data));
    yield put(getIntegrationOverview());
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Подключение обновлено',
        code: response.status || 200,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось обновить подключение');
  }
}

export function* removeIntegrationConnectionWorker(
  action: PayloadAction<number>,
) {
  const response: ApiResponse<undefined> = yield callCheckingAuth(
    API.removeIntegrationConnectionRequest,
    action.payload,
  );
  if (response.ok) {
    yield put(deleteIntegrationConnection(action.payload));
    yield put(getIntegrationOverview());
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Подключение удалено',
        code: response.status || 204,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось удалить подключение');
  }
}

export function* getExternalBookingsWorker() {
  const bookings: ExternalBooking[] = [];
  let offset = 0;
  let hasNextPage = true;

  while (hasNextPage) {
    const response: ApiResponse<ExternalBookingResponse> = yield callCheckingAuth(
      API.getExternalBookingsRequest,
      { limit: 500, offset },
    );
    if (!response.ok || !response.data) {
      const message = 'Не удалось загрузить внешние бронирования.';
      if (bookings.length) yield put(setExternalBookings(bookings));
      yield put(setExternalBookingsError(message));
      yield put(setMessage({
        status: MessageTypes.ERROR,
        message,
        code: response.status || 500,
      }));
      return;
    }

    const page = getResults(response.data);
    bookings.push(...page);
    hasNextPage = !Array.isArray(response.data) && Boolean(response.data.next);
    if (hasNextPage && page.length) offset += page.length;
    else hasNextPage = false;
  }

  yield put(setExternalBookings(bookings));
}

export function* getExternalBookingWorker(action: PayloadAction<number>) {
  const response: ApiResponse<ExternalBooking> = yield callCheckingAuth(
    API.getExternalBookingRequest,
    action.payload,
  );
  if (response.ok && response.data) {
    yield put(setSelectedExternalBooking(response.data));
  } else {
    yield* handleError(response.status, 'Не удалось загрузить внешнее бронирование');
  }
}

export function* updateExternalBookingWorker(
  action: PayloadAction<{ id: number; data: any }>,
) {
  const response: ApiResponse<ExternalBooking> = yield callCheckingAuth(
    API.updateExternalBookingRequest,
    action.payload.id,
    action.payload.data,
  );
  if (response.ok && response.data) {
    yield put(replaceExternalBooking(response.data));
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Внешнее бронирование обновлено',
        code: response.status || 200,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось обновить внешнее бронирование');
  }
}

export function* markExternalBookingViewedWorker(action: PayloadAction<number>) {
  const response: ApiResponse<ExternalBooking> = yield callCheckingAuth(
    API.updateExternalBookingViewedRequest,
    action.payload,
  );
  if (response.ok && response.data) {
    yield put(replaceExternalBookingViewed(response.data));
  }
}

export function* getIntegrationRunsWorker(action: PayloadAction<number>) {
  const response: ApiResponse<IntegrationSyncRunResponse> = yield callCheckingAuth(
    API.getIntegrationRunsRequest,
    action.payload,
  );
  if (response.ok && response.data) {
    yield put(setIntegrationRuns(getResults(response.data)));
  } else {
    yield* handleError(response.status, 'Не удалось загрузить журнал синхронизации');
  }
}

export function* getIntegrationConflictsWorker() {
  const response: ApiResponse<IntegrationConflictResponse> = yield callCheckingAuth(
    API.getIntegrationConflictsRequest,
  );
  if (response.ok && response.data) {
    yield put(setIntegrationConflicts(getResults(response.data)));
  } else {
    yield* handleError(response.status, 'Не удалось загрузить конфликты');
  }
}

export function* rotateCalendarFeedWorker(action: PayloadAction<number>) {
  const response: ApiResponse<CalendarFeed> = yield callCheckingAuth(
    API.rotateCalendarFeedRequest,
    action.payload,
  );
  if (response.ok && response.data) {
    yield put(setCalendarFeed(response.data));
    yield put(getIntegrationOverview());
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Ссылка экспорта создана',
        code: response.status || 201,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось создать ссылку экспорта');
  }
}

export function* revokeCalendarFeedWorker(action: PayloadAction<number>) {
  const response: ApiResponse<CalendarFeedRevokeResponse> = yield callCheckingAuth(
    API.revokeCalendarFeedRequest,
    action.payload,
  );
  if (response.ok) {
    yield put(clearCalendarFeed());
    yield put(getIntegrationOverview());
    yield put(
      setMessage({
        status: MessageTypes.POSITIVE,
        message: 'Ссылка экспорта отозвана',
        code: response.status || 200,
      }),
    );
  } else {
    yield* handleError(response.status, 'Не удалось отозвать ссылку экспорта');
  }
}

export function* copyCalendarFeedUrlWorker(
  action: PayloadAction<{
    rentalObjectId: number;
    onSuccess: (url: string) => void;
    onError: (message: string) => void;
  }>,
) {
  const response: ApiResponse<CalendarFeedUrlResponse> =
    yield callCheckingAuth(
      API.getCalendarFeedUrlRequest,
      action.payload.rentalObjectId,
    );
  if (response.ok && response.data?.feed_url) {
    action.payload.onSuccess(response.data.feed_url);
    yield put(finishCopyCalendarFeedUrl());
  } else {
    const responseCode = (response.data as { code?: string } | undefined)?.code;
    const fallback = responseCode === 'FEED_TOKEN_NOT_RECOVERABLE'
      ? 'Эту ссылку нельзя восстановить. Перевыпустите URL экспорта.'
      : responseCode === 'NO_ACTIVE_FEED'
        ? 'Ссылка экспорта ещё не создана.'
        : 'Не удалось получить ссылку для копирования';
    const message = responseCode === 'FEED_TOKEN_NOT_RECOVERABLE' || responseCode === 'NO_ACTIVE_FEED'
      ? fallback
      : errorText(response.status, fallback);
    action.payload.onError(message);
    yield put(finishCopyCalendarFeedUrl());
  }
}

export default function* integrationSaga() {
  yield all([
    takeLatest(getIntegrationConnections.type, getIntegrationConnectionsWorker),
    takeLatest(getIntegrationOverview.type, getIntegrationOverviewWorker),
    takeLatest(createIntegrationConnection.type, createIntegrationConnectionWorker),
    takeLatest(updateIntegrationConnection.type, updateIntegrationConnectionWorker),
    takeLatest(removeIntegrationConnection.type, removeIntegrationConnectionWorker),
    takeLatest(getExternalBookings.type, getExternalBookingsWorker),
    takeLatest(getExternalBooking.type, getExternalBookingWorker),
    takeLatest(updateExternalBooking.type, updateExternalBookingWorker),
    takeLatest(markExternalBookingViewed.type, markExternalBookingViewedWorker),
    takeLatest(getIntegrationRuns.type, getIntegrationRunsWorker),
    takeLatest(getIntegrationConflicts.type, getIntegrationConflictsWorker),
    takeLatest(rotateCalendarFeed.type, rotateCalendarFeedWorker),
    takeLatest(revokeCalendarFeed.type, revokeCalendarFeedWorker),
    takeLatest(copyCalendarFeedUrl.type, copyCalendarFeedUrlWorker),
  ]);
}
